import { useCallback, useEffect, useState } from "react";
import { client } from "../net/client";
import { useSignal } from "../net/signal";
import { fa, faNum } from "../game/cards";
import { games } from "./games";
import { authHref, useRegistered } from "./Account";
import { MatchSearching, useNow } from "./CupBits";
import { playAfterAd } from "./Ads";
import {
  INVITE_LINE, arenaStatus, at, cityCupLine, cupRef, feeFor, isArena, isGems, isInvitational, joinError, left, listWhen, phaseAt, poolLine,
  prizeLines, rules, unitOf,
  type Campaign, type City, type Detail, type Me, type Phase,
} from "./cup";

/**
 * لیگ، جام و رویداد — از نگاهِ بازیکن.
 *
 * تا امروز اینها فقط در پنلِ ادمین بودند و بازیکن هیچ‌جا نمی‌دیدشان؛ لیگی که
 * کسی از آن خبر ندارد لیگ نیست. این صفحه همان چیزی را نشان می‌دهد که سرور
 * عمومی می‌کند (`GET /v1/campaigns?upcoming=1`) و نه بیشتر: کارزارِ پیش‌نویس
 * نمی‌آید، و تمام‌شده فقط جامِ آرنا تا یک روز بعد (جدولِ نهایی و قهرمان).
 *
 * جدول حساب نمی‌خواهد — هر کسی می‌تواند ببیند چه خبر است. ولی «تو کجای
 * جدولی» با توکن می‌آید، چون رتبه‌ی هر کس مالِ خودِ اوست.
 *
 * جامِ آرنا («جامِ شبِ جمعه»، docs/friday-cup.md) صفحه‌ی خودش را دارد
 * ([[Arena]]): ثبت‌نام، شمارشِ معکوس، قاعده، جایزه‌ها و صفِ جام.
 *
 * لیگ و جامِ شهرها (docs/crowd.md، «شهرها») در فهرست نمی‌آیند — هفته‌ای ۲۴ تا‌اند. جایشان
 * بخشِ «شهرها» است: یک کاشی برای هر شهر ([[CityGrid]])، و صفحه‌ی هر شهر با لیگ و جامش.
 */

/** بنرِ خراب یا نرسیده نباید جای خالیِ شکسته بگذارد. */
const hideImg = (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = "none"; };

const KIND = { league: { fa: "لیگ", icon: "🏆" }, cup: { fa: "جام", icon: "🏅" }, event: { fa: "رویداد", icon: "🎉" } };
const gameName = (id: string) => games.find((g) => g.id === id)?.fa ?? (id ? id : "همه‌ی بازی‌ها");

export function Cups({ onBack, initial, focusCities }: { onBack: () => void; initial?: string | null; focusCities?: boolean }) {
  const [list, setList] = useState<Campaign[] | null>(null);
  // initial: کارزاری که لینکِ لندینگ یا نوارِ جامِ لابی خواسته — مستقیم جدولش باز می‌شود.
  const [open, setOpen] = useState<string | null>(initial ?? null);
  const [cities, setCities] = useState<City[]>([]);
  const [city, setCity] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const match = useSignal(client.match);
  const now = useNow();
  useEffect(() => {
    // upcoming=1: جامِ آرنای هفته‌ی بعد (ثبت‌نامش از حالا باز است) و جامِ دیشب
    // (جدولِ نهایی) هم بیایند. بی آن فقط کارزارهای زنده می‌آمدند.
    // party=1: «شب‌های مافیا» هم بیاید — اپِ کهنه مافیا ندارد و سرور جامِ دورهمی را فقط با این می‌دهد
    // gems=1: جام و لیگِ گوهری — اپِ کهنه ورودی را «سکه» می‌خواند و سرور آن‌ها را فقط با این می‌دهد
    client.rest<{ campaigns: Campaign[] }>("GET", "/v1/campaigns?upcoming=1&party=1&gems=1")
      .then((r) => setList(r.campaigns ?? []), () => setErr("به سرور وصل نشد"));
    // شهرها جدا می‌آیند؛ نبودنش (سرورِ کهنه، شهرهای خاموش) یعنی بخشی نیست.
    client.rest<{ cities: City[] }>("GET", "/v1/cities").then((r) => setCities(r.cities ?? []), () => {});
  }, []);

  if (open) return <Board id={open} onBack={() => setOpen(null)} />;
  const here = city ? cities.find((c) => c.name === city) : undefined;
  if (here) return <CityPage city={here} onOpen={setOpen} onBack={() => setCity(null)} />;
  const grid = cities.length > 0 && <CityGrid cities={cities} onPick={setCity} />;
  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← خانه</button>
        <b style={{ flex: 1 }}>لیگ و جام</b>
      </div>
      {/* صفی که از صفحه‌ی جام گرفته شد و بعد به فهرست برگشتی: «بی‌خیال» هم باید دمِ دست بماند. */}
      {match?.state === "searching" && <MatchSearching match={match} className="plate stack" />}
      {err && <div className="muted">{err}</div>}
      {/* از کارتِ شهرهای سایت (‎?cities=1) آمده: شهرها اول */}
      {focusCities && grid}
      {!err && list === null && <div className="muted">…</div>}
      {list?.length === 0 && cities.length === 0 && (
        <div className="plate stack">
          <b>الان لیگ یا جامی برپا نیست</b>
          <div className="muted">هر وقت راه بیفتد همین‌جا، روی صفحه‌ی اصلی سایت، و در صندوق پیام‌ها خبرش می‌آید.</div>
        </div>
      )}
      {list?.map((c) => <CupRow key={c.id} c={c} now={now} onOpen={setOpen} />)}
      {!focusCities && grid}
    </div>
  );
}

/** کارتِ یک کارزار در فهرست — همان شکلِ کارتِ لندینگ. */
function CupRow({ c, now, onOpen, extra }: { c: Campaign; now: number; onOpen: (id: string) => void; extra?: React.ReactNode }) {
  const arena = isArena(c);
  const invite = isInvitational(c);
  const cfgPrize = c.config?.prize_coins;
  const top = arena ? c.prizes?.[0] : typeof cfgPrize === "number" ? cfgPrize : undefined;
  return (
    <button className="cuprow" onClick={() => onOpen(c.id)}>
      <span className={"cupshot" + (c.banner ? "" : " noimg")} data-glyph={KIND[c.kind].icon}>
        {c.banner && <img className="cupbanner" src={c.banner} alt="" loading="lazy" onError={hideImg} />}
        <span className="cupkinds">
          <span className="cupkind">{KIND[c.kind].icon} {KIND[c.kind].fa}</span>
          {invite && <span className="cupkind">دعوتی</span>}
        </span>
      </span>
      <span className="cupbody">
        <b>{c.title}</b>
        {c.description && <span className="cupdesc">{c.description}</span>}
        <span className="cupchips">
          <span>{gameName(c.game)}</span>
          <span className={arena && phaseAt(c, now) === "live" ? "live" : undefined}>{listWhen(c, now)}</span>
          {typeof top === "number" && top > 0 && !invite && (
            <span className="prize">{arena ? "جایزه‌ی اول" : "جایزه"} {faNum(top)} {unitOf(c)}</span>
          )}
          {isGems(c) && !invite && !(typeof top === "number" && top > 0) && (c.entry_fee ?? 0) > 0 && (
            <span className="prize">ورودی {faNum(c.entry_fee ?? 0)} گوهر</span>
          )}
          {c.kind !== "league" && c.entries > 0 && <span>{fa(c.entries)} نفر</span>}
          {extra}
        </span>
      </span>
    </button>
  );
}

/**
 * «لیگ و جامِ شهرها»: یک کاشی برای هر شهر — نام، صدرنشینِ لیگِ این هفته، جامِ نزدیک، و
 * میزهای زنده. رنگِ هر کاشی از جای شهر در فهرست است، تا دوازده کاشی یک دیوارِ یک‌رنگ نشوند.
 */
function CityGrid({ cities, onPick }: { cities: City[]; onPick: (name: string) => void }) {
  const now = useNow();
  return (
    <section className="plate stack citysec" aria-label="لیگ و جام شهرها">
      <div>
        <b>🏙️ لیگ و جام شهرها</b>
        <div className="muted">هر شهر لیگ هفتگی خودش را دارد و هفته‌ای یک شب جام. دعوتی است؛ جدول و میزها را تماشا کن.</div>
      </div>
      <div className="citygrid">
        {cities.map((c, i) => {
          const tables = (c.league?.tables ?? 0) + (c.cup?.tables ?? 0);
          const cupLive = c.cup ? phaseAt(c.cup, now) === "live" : false;
          const lead = c.league?.leader;
          return (
            <button key={c.name} className={"citytile" + (cupLive ? " hot" : "")} style={{ "--h": (i * 29 + 250) % 360 } as React.CSSProperties}
              onClick={() => onPick(c.name)}>
              <span className="cityname">
                {c.name}
                {tables > 0 && <span className="citylive" title={`${fa(tables)} میز زنده`}>{fa(tables)} میز</span>}
              </span>
              <span className="citylead">
                {lead ? <>👑 {lead.name || "بی‌نام"} <b>{faNum(lead.points)}</b></> : <span className="muted">هنوز امتیازی نیست</span>}
              </span>
              {c.cup && cityCupLine(c.cup, now) && <span className={"citycup" + (cupLive ? " live" : "")}>{cityCupLine(c.cup, now)}</span>}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** صفحه‌ی یک شهر: لیگِ این هفته و جامش، هر کدام به جدولِ خودش. */
function CityPage({ city, onOpen, onBack }: { city: City; onOpen: (id: string) => void; onBack: () => void }) {
  const now = useNow();
  const live = (n: number) => (n > 0 ? <span className="live">{fa(n)} میز زنده</span> : null);
  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← لیگ و جام</button>
        <b style={{ flex: 1 }}>🏙️ {city.name}</b>
      </div>
      <div className="muted">بازیکن‌های {city.name} فقط با هم بازی می‌کنند. {INVITE_LINE}</div>
      {city.league && <CupRow c={city.league} now={now} onOpen={onOpen} extra={live(city.league.tables)} />}
      {city.cup && <CupRow c={city.cup} now={now} onOpen={onOpen} extra={live(city.cup.tables)} />}
    </div>
  );
}

function Board({ id, onBack }: { id: string; onBack: () => void }) {
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState("");
  // توکن که رسید دوباره بپرس: صفحه‌ای که مستقیم از لینک باز شده پیش از ورود
  // می‌پرسد، و آن‌وقت «من» خالی می‌آید — نه جای من در جدول، نه ورودیِ من.
  const pid = useSignal(client.playerId);
  const load = useCallback(() => {
    client.rest<Detail>("GET", "/v1/campaigns/" + encodeURIComponent(id))
      .then((x) => { setD(x); setErr(""); }, () => setErr("این کارزار دیگر نیست"));
  }, [id]);
  useEffect(() => { load(); }, [load, pid]);

  if (err) return <div className="page stack"><button className="hb" onClick={onBack}>← لیگ و جام</button><div className="muted">{err}</div></div>;
  if (!d) return <div className="page stack"><button className="hb" onClick={onBack}>← لیگ و جام</button><div className="muted">…</div></div>;
  if (isArena(d.campaign)) return <Arena d={d} reload={load} onBack={onBack} />;
  const c = d.campaign;
  const prize = c.config?.prize_coins;
  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← لیگ و جام</button>
        <b style={{ flex: 1 }}>{KIND[c.kind].icon} {c.title}</b>
      </div>
      <div className="plate stack">
        {c.banner && <img className="cupbanner" src={c.banner} alt="" onError={hideImg} />}
        {c.description && <div>{c.description}</div>}
        <div className="row" style={{ gap: 10 }}>
          <span className="muted">{gameName(c.game)}</span>
          <span className="muted">{left(c.ends_at_ms)}</span>
          {typeof prize === "number" && <span className="muted">جایزه {faNum(prize)} سکه</span>}
        </div>
        {c.kind === "league" && <div className="muted">هر بازی آنلاین یک امتیاز دارد و هر برد سه امتیاز دیگر. ثبت‌نام نمی‌خواهد؛ بازی کن، در جدول می‌آیی.</div>}
      </div>

      {/* رتبه‌ی صفر یعنی «هنوز در جدول نیستی» — ردیفِ «رتبه ۰» فقط گیج می‌کند. */}
      {d.me && d.me.rank > 0 && (
        <div className="plate stack" style={{ borderColor: "var(--me-gold)" }}>
          <b>جای تو در جدول</b>
          <div className="row" style={{ gap: 14 }}>
            <span>رتبه <b>{faNum(d.me.rank)}</b></span>
            <span>امتیاز <b>{faNum(d.me.points)}</b></span>
            {d.me.played > 0 && <span className="muted">{faNum(d.me.played)} بازی · {faNum(d.me.wins)} برد</span>}
          </div>
        </div>
      )}

      <div className="plate stack">
        <b>جدول</b>
        {d.board.length === 0
          ? <div className="muted">هنوز کسی در این جدول نیست. اولین نفر باش.</div>
          : d.board.map((r) => (
            <div key={r.rank} className={"row cupline" + (r.me ? " me" : "")} style={{ gap: 10 }}>
              <span className="rank">{faNum(r.rank)}</span>
              <span style={{ flex: 1 }}>{r.name || "بی‌نام"}{r.eliminated && <span className="muted"> · حذف</span>}</span>
              {r.played > 0 && <span className="muted">{faNum(r.wins)} برد</span>}
              <b>{faNum(r.points)}</b>
            </div>
          ))}
      </div>
    </div>
  );
}

/**
 * جامِ آرنا — «جامِ شبِ جمعه».
 *
 * بالا یک جمله‌ی وضعیت و **یک** دکمه، به‌حسبِ مرحله و ثبت‌نام:
 *   پیش از ثبت‌نام «ثبت‌نام — رایگان / ۲۰۰ سکه»، بعدِ ثبت‌نام و پیش از شروع
 *   «ثبت‌نام شدی» ِ خاموش، در طولِ جام «بازی در جام»، و بعدش هیچ.
 * زیرش قاعده در سه خط، جایزه‌ها، جای من، و جدول.
 */
function Arena({ d, reload, onBack }: { d: Detail; reload: () => void; onBack: () => void }) {
  const c = d.campaign;
  const now = useNow();
  const ph = phaseAt(c, now);
  const me = d.me;
  const champ = ph === "ended" ? d.board.find((r) => r.rank === 1 && r.points > 0) : undefined;
  const invite = isInvitational(c);
  // رقابتِ دعوتی جایزه‌ای برای اعلام ندارد.
  const prizes = invite ? [] : prizeLines(c.prizes ?? []);
  const unit = unitOf(c);
  const pool = poolLine(c);
  return (
    <div className="page stack">
      <div className="row">
        <button className="hb" onClick={onBack}>← لیگ و جام</button>
        <b style={{ flex: 1 }}>{KIND[c.kind].icon} {c.title}</b>
      </div>

      <div className="plate stack arena">
        {c.banner && <img className="cupbanner arenabanner" src={c.banner} alt="" onError={hideImg} />}
        <div className={"arenastatus " + ph} role="status">{arenaStatus(c, now)}</div>
        {champ && <div className="champ">🏆 قهرمان: <b>{champ.name || "بی‌نام"}</b> با {faNum(champ.points)} امتیاز</div>}
        {ph === "ended" && !champ && <div className="muted">کسی امتیازی نگرفت.</div>}
        <div className="muted">
          {invite && <span className="cuptag">دعوتی</span>}
          {/* «،» و نه «·» پیش از رقم: «· ۱ نفر» «۱۰ نفر» دیده می‌شد */}
          {gameName(c.game)}، {invite
            ? `${fa(c.entries)} بازیکن دعوتی`
            : c.entries > 0 ? `${fa(c.entries)} نفر ثبت‌نام کرده‌اند` : "هنوز کسی ثبت‌نام نکرده"}
        </div>
        <Action c={c} me={me} ph={ph} reload={reload} />
      </div>

      {(ph === "live" || ph === "closing") && <CupTables id={c.id} />}

      <div className="plate stack">
        <b>قاعده</b>
        <ul className="arenarules">{rules(c).map((l) => <li key={l}>{l}</li>)}</ul>
      </div>

      {(prizes.length > 0 || pool) && (
        <div className="plate stack">
          <b>جایزه‌ها</b>
          {prizes.length > 0 && (
            <div>
              {prizes.map((p) => (
                <div key={p.label} className="row cupline" style={{ gap: 10 }}>
                  <span style={{ flex: 1 }}>{p.label}</span>
                  <b>{faNum(p.coins)} {unit}</b>
                </div>
              ))}
            </div>
          )}
          {pool && <div className="muted">{pool}</div>}
          <div className="muted">جایزه فقط به کسی می‌رسد که امتیاز گرفته و وقتی آخرین میز جام تمام شود، به کیفش اضافه می‌شود.</div>
        </div>
      )}

      {me && me.rank > 0 && (
        <div className="plate stack" style={{ borderColor: "var(--me-gold)" }}>
          <b>جای تو در جدول</b>
          <div className="row" style={{ gap: 14, flexWrap: "wrap" }}>
            <span>رتبه <b>{faNum(me.rank)}</b> از {fa(c.entries)}</span>
            <span>امتیاز <b>{faNum(me.points)}</b></span>
            {(me.streak ?? 0) >= 2 && <span>🔥 {fa(me.streak ?? 0)} برد پشت‌سرِهم</span>}
            {me.played > 0 && <span className="muted">{faNum(me.played)} بازی · {faNum(me.wins)} برد</span>}
          </div>
        </div>
      )}

      <div className="plate stack">
        <b>{ph === "ended" ? "جدول نهایی" : "جدول"}</b>
        {d.board.length === 0
          ? <div className="muted">{ph === "upcoming" ? "جدول با اولین میز جام پر می‌شود." : "هنوز کسی در این جدول نیست."}</div>
          : d.board.map((r) => (
            <div key={r.rank} className={"row cupline" + (r.me ? " me" : "")} style={{ gap: 10 }}>
              <span className="rank">{faNum(r.rank)}</span>
              <span className="nm">
                {r.name || "بی‌نام"}
                {/* دو برد پشتِ هم یعنی بردِ بعدی دوبرابر است — همان چیزی که بقیه باید بدانند. */}
                {(r.streak ?? 0) >= 2 && <span title={`${fa(r.streak ?? 0)} برد پشت‌سرِهم`}> 🔥</span>}
              </span>
              {r.played > 0 && <span className="muted">{faNum(r.wins)} برد</span>}
              {(r.prize ?? 0) > 0 && <span className="cupprize">{faNum(r.prize ?? 0)} {unit}</span>}
              <b>{faNum(r.points)}</b>
            </div>
          ))}
      </div>
    </div>
  );
}

type LiveTable = { table_id?: string; game: string; cup?: string; seats: { name: string }[] };

/**
 * میزهای زنده‌ی همین جام، برای تماشا — از `/v1/live` که نشانِ جامِ هر میز را دارد. هر
 * پانزده ثانیه، فقط وقتی برگه دیده می‌شود. تماشا سرور را می‌خواهد: مرورگرِ بی‌حساب
 * همان لحظه‌ی «تماشا» مهمانش را می‌گیرد و نه زودتر (همان قاعده‌ی صفحه‌ی تماشا).
 */
function CupTables({ id }: { id: string }) {
  const [tables, setTables] = useState<LiveTable[]>([]);
  const [want, setWant] = useState<string | null>(null);
  const conn = useSignal(client.state);
  useEffect(() => {
    const ask = () => {
      if (document.visibilityState !== "visible") return;
      client.rest<{ tables: LiveTable[] }>("GET", "/v1/live")
        .then((r) => setTables((r.tables ?? []).filter((t) => t.cup === id && t.table_id)), () => {});
    };
    ask();
    const t = setInterval(ask, 15_000);
    return () => clearInterval(t);
  }, [id]);
  useEffect(() => {
    if (!want) return;
    if (conn === "signedOut") void client.connect({ create: true });
    else if (conn === "online") { const id = want; setWant(null); playAfterAd(() => client.watchTable(id), "watch"); }
  }, [want, conn]);
  if (tables.length === 0) return null;
  return (
    <div className="plate stack">
      <b>میزهای زنده</b>
      {tables.map((t) => (
        <div key={t.table_id} className="row cupline" style={{ gap: 10 }}>
          <span className="nm">{t.seats.map((s) => s.name).filter(Boolean).join("، ")}</span>
          <button className="btn teal" onClick={() => setWant(t.table_id!)}>تماشا</button>
        </div>
      ))}
    </div>
  );
}

/** تنها دکمه‌ی صفحه‌ی جام، به‌حسبِ مرحله و ثبت‌نام. */
function Action({ c, me, ph, reload }: { c: Campaign; me: Me | null; ph: Phase; reload: () => void }) {
  const registered = useRegistered();
  const username = useSignal(client.username);
  const match = useSignal(client.match);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // رقابتِ دعوتی ثبت‌نام ندارد: دعوت‌شده‌ها از پیش در جدول‌اند (me.joined) و
  // بقیه فقط تماشا می‌کنند — دکمه‌ای که همیشه «نه» بگوید بدتر از نبودنش است.
  const invite = isInvitational(c);
  if (invite && !me?.joined) return <div className="muted">{INVITE_LINE}</div>;
  // جامِ تمام‌شده یا در حالِ جمع‌بندی کاری برای بازیکن ندارد؛ جمله‌ی وضعیت بس است.
  if (ph !== "upcoming" && ph !== "live") return null;
  const fee = feeFor(c, me);

  if (me?.joined) {
    if (ph === "upcoming") {
      return (
        <button className="btn ghost" disabled>
          {invite ? "دعوت شدی" : "ثبت‌نام شدی"} · شروع {at(c.starts_at_ms)}
        </button>
      );
    }
    // در صف (این جام، یا صفِ دیگری که هنوز لغو نشده): همان صف و «بی‌خیال»‌اش.
    if (match?.state === "searching") return <MatchSearching match={match} />;
    return <button className="btn bigbtn" onClick={() => playAfterAd(() => client.cupMatch(cupRef(c)), "cup")}>{c.kind === "league" ? "بازی در لیگ" : "بازی در جام"}</button>;
  }

  const join = async () => {
    // مهمان را تا سرور نفرستیم: جواب از پیش معلوم است و راهِ درستش صفحه‌ی حساب است.
    if (!registered) { setErr("account_required"); return; }
    setBusy(true);
    setErr("");
    try {
      // gems=1: این کلاینت ورودیِ گوهری را می‌شناسد — سرور بی آن جامِ گوهری را نمی‌پذیرد.
      await client.rest("POST", `/v1/campaigns/${encodeURIComponent(c.id)}/join?gems=1`);
      reload();
    } catch (e) {
      const code = e instanceof Error ? e.message : "";
      setErr(code);
      if (code === "cup_closed" || code === "not_found" || code === "invitational") reload();
    } finally {
      setBusy(false);
    }
  };
  const needAccount = err === "account_required" || err === "unauthorized";
  const unit = unitOf(c);
  return (
    <div className="stack">
      {/* بی توکن «من» نداریم و نمی‌دانیم اولین جامِ این آدم است یا نه؛ عددِ غلط نمی‌گوییم. */}
      <button className="btn bigbtn" disabled={busy} onClick={() => void join()}>
        {me ? `ثبت‌نام — ${fee > 0 ? faNum(fee) + " " + unit : "رایگان"}` : "ثبت‌نام"}
      </button>
      {me?.first_free && <div className="muted">اولین جامت مهمان ماست.</div>}
      {!me && (c.entry_fee ?? 0) > 0 && (
        <div className="muted">اولین جام هر بازیکن رایگان است؛ از آن به بعد ورودی {faNum(c.entry_fee ?? 0)} {unit}.</div>
      )}
      {err && <div className="err" role="alert">{joinError(err, fee)}</div>}
      {/* تبدیلِ سکه به گوهر از ۳۰ اکتبرِ ۲۰۲۶ فقط در اپ است؛ #appbuy ِ فروشگاه نشانِ بازار و مایکت را دارد. */}
      {err === "insufficient_gems" && <a className="btn ghost" href="/shop.html#appbuy">تبدیل سکه به گوهر در اپ</a>}
      {needAccount && (
        <a className="btn ghost" href={authHref(username ? "login" : "register")}>
          {username ? "تأیید ایمیل" : "ساختن حساب"}
        </a>
      )}
    </div>
  );
}
